import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, TrendingUp, TrendingDown, Minus, DollarSign, Hash, Timer, Gem, Trophy, Medal, ArrowDown, Building, Users, ArrowUpRight, ArrowDownRight, ArrowRight, Target, X } from 'lucide-react';
import { ScopeType, DashboardType, TimePeriod, getEnrichedLeaderboardData, getInitials, EnrichedLeaderboardEntry, CURRENT_USER_DIVISION } from '../data/leaderboardData';
const CURRENT_USER_NAME = 'Katrina Bullock';
// Deterministic profile photo mapping
const PROFILE_PHOTOS: Record<string, string> = {
  'Katrina Bullock': 'https://randomuser.me/api/portraits/women/44.jpg',
  'Melanie Rayis': 'https://randomuser.me/api/portraits/women/68.jpg',
  'LaMonte Stinnette': 'https://randomuser.me/api/portraits/men/32.jpg',
  'Bianca Payton': 'https://randomuser.me/api/portraits/women/65.jpg',
  'Daniel McCarthy': 'https://randomuser.me/api/portraits/men/46.jpg',
  'Lucas Rohr': 'https://randomuser.me/api/portraits/men/22.jpg',
  'Charles Ryle': 'https://randomuser.me/api/portraits/men/36.jpg',
  'Zaid Shuwayhat': 'https://randomuser.me/api/portraits/men/75.jpg',
  'Antonio Hayes': 'https://randomuser.me/api/portraits/men/52.jpg',
  'Mike Kounini': 'https://randomuser.me/api/portraits/men/11.jpg',
  'Jasmine Torres': 'https://randomuser.me/api/portraits/women/33.jpg',
  'Derek Walton': 'https://randomuser.me/api/portraits/men/85.jpg'
};
function getProfilePhoto(name: string): string | null {
  return PROFILE_PHOTOS[name] || null;
}
const DASHBOARD_TYPES: {
  key: DashboardType;
  label: string;
  icon: typeof DollarSign;
  description: string;
}[] = [{
  key: 'volume',
  label: 'Volume',
  icon: DollarSign,
  description: 'Total loan volume'
}, {
  key: 'units',
  label: 'Units',
  icon: Hash,
  description: 'Loans closed'
}, {
  key: 'speed',
  label: 'Speed to Close',
  icon: Timer,
  description: 'Avg. days to close'
}];
const TIME_PERIODS: {
  key: TimePeriod;
  label: string;
  prevLabel: string | null;
}[] = [{
  key: 'this_month',
  label: 'This Month',
  prevLabel: 'Last Mo.'
}, {
  key: 'last_month',
  label: 'Last Month',
  prevLabel: 'Prev 3 Mo.'
}, {
  key: 'last_3_months',
  label: 'Last 3 Months',
  prevLabel: 'YTD'
}, {
  key: 'ytd',
  label: 'Year to Date',
  prevLabel: null
}];
const SCOPE_CONFIG: {
  key: ScopeType;
  label: string;
  icon: typeof Building;
  subtitle: string;
}[] = [{
  key: 'company',
  label: 'Company',
  icon: Building,
  subtitle: 'All producers'
}, {
  key: 'division',
  label: 'Division',
  icon: Users,
  subtitle: 'Your division'
}, {
  key: 'team',
  label: 'Team',
  icon: Users,
  subtitle: 'Your team'
}];
function formatValue(value: number, type: DashboardType): string {
  if (type === 'volume') {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  }
  if (type === 'speed') {
    return `${value} days`;
  }
  return value.toString();
}
function formatDelta(current: number, previous: number, type: DashboardType): {
  text: string;
  isPositive: boolean;
  isNeutral: boolean;
} {
  const diff = current - previous;
  const isNeutral = diff === 0;
  // For speed, lower is better, so a negative diff (fewer days) is "positive" performance
  const isPositivePerformance = type === 'speed' ? diff < 0 : diff > 0;
  let formattedDiff = '';
  if (type === 'volume') {
    const absDiff = Math.abs(diff);
    if (absDiff >= 1000000) {
      formattedDiff = `$${(absDiff / 1000000).toFixed(1)}M`;
    } else {
      formattedDiff = `$${(absDiff / 1000).toFixed(0)}K`;
    }
  } else if (type === 'speed') {
    formattedDiff = `${Math.abs(diff)}d`;
  } else {
    formattedDiff = Math.abs(diff).toString();
  }
  const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
  return {
    text: `${sign}${formattedDiff}`,
    isPositive: isPositivePerformance,
    isNeutral
  };
}
function getBadgeStyle(badge: 'Gold' | 'Platinum' | 'Diamond') {
  switch (badge) {
    case 'Diamond':
      return {
        bg: 'bg-blue-500/15',
        text: 'text-blue-400',
        border: 'border-blue-500/25'
      };
    case 'Platinum':
      return {
        bg: 'bg-purple-500/15',
        text: 'text-purple-400',
        border: 'border-purple-500/25'
      };
    case 'Gold':
      return {
        bg: 'bg-amber-500/15',
        text: 'text-amber-400',
        border: 'border-amber-500/25'
      };
  }
}
function getBadgeIcon(badge: 'Gold' | 'Platinum' | 'Diamond') {
  switch (badge) {
    case 'Diamond':
      return <Gem size={13} className="text-blue-400" data-id="element-446" />;
    case 'Platinum':
      return <Trophy size={13} className="text-purple-400" data-id="element-447" />;
    case 'Gold':
      return <Medal size={13} className="text-amber-400" data-id="element-448" />;
  }
}
export function LeaderboardView() {
  const [scope, setScope] = useState<ScopeType>('company');
  const [dashboardType, setDashboardType] = useState<DashboardType>('volume');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('this_month');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const data = getEnrichedLeaderboardData(scope, dashboardType, timePeriod);
  const isSpeed = dashboardType === 'speed';
  const isDivisionScope = scope === 'division';
  const valueLabel = dashboardType === 'volume' ? 'Volume' : dashboardType === 'units' ? 'Units' : 'Avg. Days';
  const getSubtitle = () => {
    switch (scope) {
      case 'company':
        return 'Top producers across the company';
      case 'division':
        return 'Division rankings across the company';
      case 'team':
        return 'Top producers on your team';
    }
  };
  const activeTimePeriodConfig = TIME_PERIODS.find(t => t.key === timePeriod)!;
  const prevPeriodLabel = activeTimePeriodConfig.prevLabel;
  // Current user comparison logic
  const currentUserEntry = data.find(e => e.name === (isDivisionScope ? CURRENT_USER_DIVISION : CURRENT_USER_NAME));
  const numberOneEntry = data[0];
  let comparisonText = '';
  if (currentUserEntry && numberOneEntry) {
    if (currentUserEntry.rank === 1) {
      if (isDivisionScope) {
        comparisonText = `${CURRENT_USER_DIVISION} is ranked #1! Keep up the great work!`;
      } else {
        comparisonText = "You're #1! Keep up the great work!";
      }
    } else {
      const diff = isSpeed ? currentUserEntry.value - numberOneEntry.value : numberOneEntry.value - currentUserEntry.value;
      let diffStr = '';
      if (dashboardType === 'volume') {
        diffStr = diff >= 1000000 ? `$${(diff / 1000000).toFixed(1)}M` : `$${(diff / 1000).toFixed(0)}K`;
      } else if (dashboardType === 'speed') {
        diffStr = `${diff} days`;
      } else {
        diffStr = `${diff} units`;
      }
      if (isDivisionScope) {
        comparisonText = `${CURRENT_USER_DIVISION} is ranked #${currentUserEntry.rank} — ${diffStr} behind #1 ${numberOneEntry.name}`;
      } else {
        comparisonText = `You are ranked #${currentUserEntry.rank} — ${diffStr} behind #1 ${numberOneEntry.name}`;
      }
    }
  }
  return <div className="w-full relative" data-id="element-449">
      {/* Decorative background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-accent-400/10 blur-[120px] rounded-full pointer-events-none" data-id="element-450" />

      <div className="px-8 py-8 relative z-10" data-id="element-451">
        <div className="w-full" data-id="element-452">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-10" data-id="element-453">
            <div className="inline-flex items-center justify-center gap-3 mb-3" data-id="element-454">
              <motion.div animate={{
              y: [0, -4, 0],
              filter: ['drop-shadow(0 0 0px rgba(34,211,238,0))', 'drop-shadow(0 0 10px rgba(34,211,238,0.5))', 'drop-shadow(0 0 0px rgba(34,211,238,0))']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-455">
                <Trophy size={36} className="text-accent-400" data-id="element-456" />
              </motion.div>
              <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight" data-id="element-457">
                Leaderboard
              </h1>
            </div>
            <p className="text-base text-slate-400" data-id="element-458">{getSubtitle()}</p>
          </motion.div>

          {/* Dashboard Type Selector (Cards) */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="grid grid-cols-3 gap-6 mb-8 max-w-4xl mx-auto" data-id="element-459">
            {DASHBOARD_TYPES.map(({
            key,
            label,
            icon: Icon,
            description
          }) => <button key={key} onClick={() => setDashboardType(key)} className={`relative flex flex-col items-center text-center gap-2 p-5 rounded-2xl border transition-all duration-300 ${dashboardType === key ? 'bg-navy-800/80 border-accent-400/50 text-white shadow-[0_0_20px_rgba(34,211,238,0.15)]' : 'bg-navy-800/40 border-white/[0.06] text-slate-400 hover:bg-navy-800/60 hover:border-white/[0.1]'}`} data-id="element-460">
                {dashboardType === key && <motion.div layoutId="dbTypeCard" className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-accent-400 pointer-events-none" transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} data-id="element-461" />}
                <div className={`p-3 rounded-xl ${dashboardType === key ? 'bg-accent-400/20 text-accent-400' : 'bg-navy-700/50 text-slate-400'}`} data-id="element-462">
                  <Icon size={24} data-id="element-463" />
                </div>
                <div data-id="element-464">
                  <div className="font-bold text-base mb-1" data-id="element-465">{label}</div>
                  <div className={`text-sm ${dashboardType === key ? 'text-accent-400/80' : 'text-slate-500'}`} data-id="element-466">
                    {description}
                  </div>
                </div>
              </button>)}
          </motion.div>

          {/* Scope & Time Controls Row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8 bg-navy-800/30 p-3 rounded-2xl border border-white/[0.04] max-w-5xl mx-auto" data-id="element-467">
            {/* Scope Selector */}
            <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className="flex items-center" data-id="element-468">
              <div className="inline-flex bg-navy-900/50 p-1.5 rounded-xl border border-white/[0.06]" data-id="element-469">
                {SCOPE_CONFIG.map(({
                key,
                label,
                icon: Icon
              }) => <button key={key} onClick={() => setScope(key)} className={`relative flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-colors ${scope === key ? 'text-navy-950' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-470">
                    {scope === key && <motion.div layoutId="scopeType" className="absolute inset-0 bg-accent-400 rounded-lg shadow-[0_0_15px_rgba(34,211,238,0.4)]" transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 30
                }} data-id="element-471" />}
                    <span className="relative z-10 flex items-center gap-2" data-id="element-472">
                      <Icon size={18} data-id="element-473" />
                      {label}
                    </span>
                  </button>)}
              </div>
            </motion.div>

            {/* Time Period Tabs */}
            <motion.div initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3
          }} className="flex items-center" data-id="element-474">
              <div className="inline-flex bg-navy-900/50 p-1.5 rounded-xl border border-white/[0.04]" data-id="element-475">
                {TIME_PERIODS.map(({
                key,
                label
              }) => <button key={key} onClick={() => setTimePeriod(key)} className={`relative px-5 py-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${timePeriod === key ? 'text-accent-400' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-476">
                    {timePeriod === key && <motion.div layoutId="timePeriod" className="absolute inset-0 bg-navy-800 rounded-lg border border-accent-400/30 shadow-[0_0_10px_rgba(34,211,238,0.15)]" transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 30
                }} data-id="element-477" />}
                    <span className="relative z-10" data-id="element-478">{label}</span>
                  </button>)}
              </div>
            </motion.div>
          </div>

          {/* Speed note */}
          <AnimatePresence data-id="element-479">
            {isSpeed && <motion.div initial={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} animate={{
            opacity: 1,
            height: 'auto',
            marginBottom: 24
          }} exit={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} className="flex items-center justify-center overflow-hidden" data-id="element-480">
                <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500/10 border border-emerald-500/20" data-id="element-481">
                  <ArrowDown size={16} className="text-emerald-400" data-id="element-482" />
                  <span className="text-sm font-semibold text-emerald-400" data-id="element-483">
                    Lower is better
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Current User Comparison Banner */}
          <AnimatePresence data-id="element-484">
            {comparisonText && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="mb-6 flex justify-center" data-id="element-485">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-accent-400/10 border border-accent-400/30 shadow-[0_0_20px_rgba(34,211,238,0.1)]" data-id="element-486">
                  <Target size={20} className="text-accent-400" data-id="element-487" />
                  <span className="text-base font-semibold text-accent-400" data-id="element-488">
                    {comparisonText}
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Single Leaderboard */}
          <AnimatePresence mode="wait" data-id="element-489">
            <motion.div key={`${scope}-${dashboardType}-${timePeriod}`} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-490">
              <div className="bg-navy-800/40 backdrop-blur-md rounded-3xl border border-white/[0.08] shadow-2xl overflow-hidden" data-id="element-491">
                {/* Column Headers */}
                <div className="flex items-center gap-6 px-8 py-5 border-b border-white/[0.08] bg-navy-900/50" data-id="element-492">
                  <div className="w-14 text-center text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-493">
                    Rank
                  </div>
                  <div className="flex-1 text-xs font-bold text-slate-400 uppercase tracking-widest pl-2" data-id="element-494">
                    {isDivisionScope ? 'Division' : 'Producer'}
                  </div>
                  <div className="w-32 text-center text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-495">
                    Badge
                  </div>
                  <div className="w-48 text-right text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-496">
                    {prevPeriodLabel ? `${prevPeriodLabel} vs Now` : 'Trend'}
                  </div>
                  <div className="w-40 text-right text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-497">
                    {valueLabel}
                  </div>
                </div>

                {/* Rows */}
                <div className="divide-y divide-white/[0.04]" data-id="element-498">
                  {data.map((entry, i) => {
                  const badgeStyle = getBadgeStyle(entry.badge);
                  const isTop3 = entry.rank <= 3;
                  const isCurrentUser = entry.name === (isDivisionScope ? CURRENT_USER_DIVISION : CURRENT_USER_NAME);
                  const isSelected = selectedUser === entry.name;
                  const rankStyles: Record<string, string> = {
                    '1': 'bg-amber-500/20 text-amber-400 border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.2)]',
                    '2': 'bg-slate-300/15 text-slate-300 border-slate-300/30',
                    '3': 'bg-orange-600/15 text-orange-400 border-orange-600/30'
                  };
                  const rankStyle = rankStyles[String(entry.rank)] || 'bg-navy-800/80 text-slate-400 border-white/[0.08]';
                  // Delta calculations
                  let deltaInfo = null;
                  if (entry.previousValue !== null) {
                    deltaInfo = formatDelta(entry.value, entry.previousValue, dashboardType);
                  }
                  // Rank change calculations
                  let rankChangeIcon = <Minus size={14} className="text-slate-600" data-id="element-499" />;
                  let rankChangeText = '';
                  let rankChangeColor = 'text-slate-500';
                  if (entry.previousRank !== null) {
                    const rankDiff = entry.previousRank - entry.rank; // Positive means improved rank (e.g. 5 -> 3 = +2)
                    if (rankDiff > 0) {
                      rankChangeIcon = <ArrowUpRight size={14} className="text-emerald-400" data-id="element-500" />;
                      rankChangeText = rankDiff.toString();
                      rankChangeColor = 'text-emerald-400';
                    } else if (rankDiff < 0) {
                      rankChangeIcon = <ArrowDownRight size={14} className="text-red-400" data-id="element-501" />;
                      rankChangeText = Math.abs(rankDiff).toString();
                      rankChangeColor = 'text-red-400';
                    } else {
                      rankChangeIcon = <ArrowRight size={14} className="text-slate-500" data-id="element-502" />;
                      rankChangeText = '-';
                    }
                  }
                  return <div key={entry.name} className="flex flex-col" data-id="element-503">
                        <motion.div onClick={() => setSelectedUser(isSelected ? null : entry.name)} initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.05 + i * 0.04
                    }} className={`flex items-center gap-6 px-8 py-5 transition-colors relative group cursor-pointer hover:bg-white/[0.04] ${isTop3 ? 'bg-white/[0.015]' : ''} ${isCurrentUser ? 'bg-accent-400/[0.08] hover:bg-accent-400/[0.12] ring-1 ring-inset ring-accent-400/40 shadow-[inset_0_0_20px_rgba(34,211,238,0.1)]' : ''} ${isSelected && !isCurrentUser ? 'bg-white/[0.04]' : ''}`} data-id="element-504">
                          {/* Rank */}
                          <div className="w-14 flex flex-col items-center gap-1 shrink-0" data-id="element-505">
                            <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center text-lg font-bold tabular-nums transition-transform group-hover:scale-105 ${rankStyle}`} data-id="element-506">
                              {entry.rank === 1 ? <Crown size={22} className="text-amber-400" data-id="element-507" /> : entry.rank}
                            </div>
                            {entry.previousRank !== null && <div className={`flex items-center gap-0.5 text-[10px] font-bold ${rankChangeColor}`} data-id="element-508">
                                {rankChangeIcon}
                                {rankChangeText}
                              </div>}
                          </div>

                          {/* Producer Info */}
                          <div className="flex-1 min-w-0 flex items-center gap-4 pl-2" data-id="element-509">
                            {/* Avatar */}
                            {isDivisionScope ? <div className={`w-12 h-12 rounded-full ${entry.avatarColor} border-2 border-white/[0.15] flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg`} data-id="element-510">
                                <Building size={20} data-id="element-511" />
                              </div> : getProfilePhoto(entry.name) ? <img src={getProfilePhoto(entry.name)!} alt={entry.name} className="w-12 h-12 rounded-full border-2 border-white/[0.15] shrink-0 shadow-lg object-cover" data-id="element-512" /> : <div className={`w-12 h-12 rounded-full ${entry.avatarColor} border-2 border-white/[0.15] flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg`} data-id="element-513">
                                {getInitials(entry.name)}
                              </div>}

                            <div className="flex flex-col justify-center" data-id="element-514">
                              <div className="flex items-center gap-3" data-id="element-515">
                                <span className={`${isDivisionScope ? 'text-xl' : 'text-lg'} font-semibold truncate ${isCurrentUser ? 'text-accent-400 font-bold' : isTop3 ? 'text-white font-bold' : 'text-slate-200'}`} data-id="element-516">
                                  {entry.name}
                                </span>
                                {isCurrentUser && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-accent-400 bg-accent-400/15 border border-accent-400/30 px-2 py-0.5 rounded-md shadow-[0_0_10px_rgba(34,211,238,0.2)]" data-id="element-517">
                                    {isDivisionScope ? 'Your Division' : 'You'}
                                  </span>}
                              </div>
                              {!isDivisionScope && <span className="text-sm text-slate-500 font-medium mt-0.5" data-id="element-518">
                                  {entry.region}
                                </span>}
                            </div>
                          </div>

                          {/* Badge */}
                          <div className="w-32 flex justify-center shrink-0" data-id="element-519">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${badgeStyle.bg} ${badgeStyle.text} ${badgeStyle.border}`} data-id="element-520">
                              {getBadgeIcon(entry.badge)}
                              {entry.badge}
                            </span>
                          </div>

                          {/* Mini Stats (Previous vs Now) */}
                          <div className="w-48 flex flex-col items-end justify-center shrink-0" data-id="element-521">
                            {entry.previousValue !== null && deltaInfo ? <>
                                <div className="flex items-center gap-2 mb-1" data-id="element-522">
                                  <span className="text-xs text-slate-500 font-medium" data-id="element-523">
                                    {prevPeriodLabel}:
                                  </span>
                                  <span className="text-sm text-slate-300 font-semibold tabular-nums" data-id="element-524">
                                    {formatValue(entry.previousValue, dashboardType)}
                                  </span>
                                </div>
                                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${deltaInfo.isNeutral ? 'bg-slate-500/10 text-slate-400' : deltaInfo.isPositive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`} data-id="element-525">
                                  {deltaInfo.isNeutral ? <Minus size={12} data-id="element-526" /> : deltaInfo.isPositive ? <TrendingUp size={12} data-id="element-527" /> : <TrendingDown size={12} data-id="element-528" />}
                                  {deltaInfo.text}
                                </div>
                              </> : <div className="text-sm text-slate-600 italic" data-id="element-529">
                                No prior data
                              </div>}
                          </div>

                          {/* Current Value */}
                          <div className="w-40 text-right shrink-0" data-id="element-530">
                            <span className="text-2xl font-bold text-white tabular-nums tracking-tight" data-id="element-531">
                              {formatValue(entry.value, dashboardType)}
                            </span>
                          </div>
                        </motion.div>

                        {/* Expandable Comparison Panel */}
                        <AnimatePresence data-id="element-532">
                          {isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden bg-navy-900/80 backdrop-blur-md border-t border-accent-400/20 relative shadow-[inset_0_4px_20px_rgba(0,0,0,0.2)]" data-id="element-533">
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent-400/50 shadow-[0_0_10px_rgba(34,211,238,0.5)]" data-id="element-534" />
                              <button onClick={() => setSelectedUser(null)} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-535">
                                <X size={16} data-id="element-536" />
                              </button>

                              <div className="px-12 py-8" data-id="element-537">
                                {isCurrentUser ?
                          // Self Stats Summary - Compact Row
                          <div className="flex items-center justify-between gap-4" data-id="element-538">
                                    <div className="flex items-center gap-4" data-id="element-539">
                                      <div className="w-12 h-12 rounded-full bg-accent-400/20 border border-accent-400/50 flex items-center justify-center shadow-[0_0_15px_rgba(34,211,238,0.2)]" data-id="element-540">
                                        {isDivisionScope ? <Building size={20} className="text-accent-400" data-id="element-541" /> : <Target size={20} className="text-accent-400" data-id="element-542" />}
                                      </div>
                                      <div data-id="element-543">
                                        <h3 className="text-lg font-bold text-white leading-tight" data-id="element-544">
                                          {isDivisionScope ? `${CURRENT_USER_DIVISION} Performance` : 'Your Scorecard'}
                                        </h3>
                                        <p className="text-xs text-slate-400" data-id="element-545">
                                          {isDivisionScope ? 'Division stats' : 'Personal stats'}{' '}
                                          for this period
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3" data-id="element-546">
                                      <div className="bg-navy-800/60 rounded-lg px-4 py-2 border border-white/[0.05] flex flex-col items-center min-w-[80px]" data-id="element-547">
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-548">
                                          Rank
                                        </div>
                                        <div className="text-base font-bold text-white" data-id="element-549">
                                          #{entry.rank}{' '}
                                          <span className="text-xs text-slate-500 font-normal" data-id="element-550">
                                            / {data.length}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="bg-navy-800/60 rounded-lg px-4 py-2 border border-white/[0.05] flex flex-col items-center min-w-[100px]" data-id="element-551">
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-552">
                                          {valueLabel}
                                        </div>
                                        <div className="text-base font-bold text-white" data-id="element-553">
                                          {formatValue(entry.value, dashboardType)}
                                        </div>
                                      </div>
                                      <div className="bg-navy-800/60 rounded-lg px-4 py-2 border border-white/[0.05] flex flex-col items-center min-w-[80px]" data-id="element-554">
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-555">
                                          Trend
                                        </div>
                                        <div className="text-base font-bold text-white flex items-center gap-1" data-id="element-556">
                                          {rankChangeIcon}
                                          <span className={rankChangeColor} data-id="element-557">
                                            {rankChangeText}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="bg-navy-800/60 rounded-lg px-4 py-2 border border-white/[0.05] flex flex-col items-center min-w-[100px]" data-id="element-558">
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-559">
                                          Tier
                                        </div>
                                        <div className="text-sm font-bold text-white flex items-center gap-1.5 mt-0.5" data-id="element-560">
                                          {getBadgeIcon(entry.badge)}
                                          {entry.badge}
                                        </div>
                                      </div>
                                    </div>
                                  </div> : !currentUserEntry ?
                          // Current user not in data
                          <div className="text-center py-4 text-slate-400" data-id="element-561">
                                    {isDivisionScope ? `Your division does not have data in this period to compare against ${entry.name}.` : `You do not have data in this period to compare against ${entry.name}.`}
                                  </div> :
                          // Head-to-Head Matchup Bar
                          <div className="flex flex-col gap-5 max-w-5xl mx-auto" data-id="element-562">
                                    {/* Row 1: Header */}
                                    <div className="flex items-center justify-between" data-id="element-563">
                                      {/* Left: You (Blue) */}
                                      <div className="flex items-center gap-3" data-id="element-564">
                                        {isDivisionScope ? <div className="w-12 h-12 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center text-white shadow-[0_0_12px_rgba(59,130,246,0.4)]" data-id="element-565">
                                            <Building size={20} data-id="element-566" />
                                          </div> : getProfilePhoto(currentUserEntry.name) ? <img src={getProfilePhoto(currentUserEntry.name)!} alt={currentUserEntry.name} className="w-12 h-12 rounded-full border-2 border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.4)] object-cover" data-id="element-567" /> : <div className="w-12 h-12 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center text-white font-bold text-sm shadow-[0_0_12px_rgba(59,130,246,0.4)]" data-id="element-568">
                                            {getInitials(currentUserEntry.name)}
                                          </div>}
                                        <div className="flex flex-col" data-id="element-569">
                                          <div className="flex items-center gap-2" data-id="element-570">
                                            <span className="text-base font-bold text-white" data-id="element-571">
                                              {currentUserEntry.name}
                                            </span>
                                            <span className="text-[9px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/15 border border-blue-500/30 px-1.5 py-0.5 rounded" data-id="element-572">
                                              {isDivisionScope ? 'Your Div' : 'You'}
                                            </span>
                                          </div>
                                          <div className="text-xs text-slate-400 font-medium" data-id="element-573">
                                            Rank #{currentUserEntry.rank}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Center: VS Badge */}
                                      <div className="px-4 py-1.5 rounded-full bg-navy-800 border border-white/10 text-sm font-bold text-slate-300 tracking-widest shadow-inner" data-id="element-574">
                                        VS
                                      </div>

                                      {/* Right: Them (Red) */}
                                      <div className="flex items-center gap-3 text-right" data-id="element-575">
                                        <div className="flex flex-col items-end" data-id="element-576">
                                          <span className="text-base font-bold text-white" data-id="element-577">
                                            {entry.name}
                                          </span>
                                          <div className="text-xs text-slate-400 font-medium" data-id="element-578">
                                            Rank #{entry.rank}
                                          </div>
                                        </div>
                                        {isDivisionScope ? <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center text-white shadow-[0_0_12px_rgba(239,68,68,0.4)]" data-id="element-579">
                                            <Building size={20} data-id="element-580" />
                                          </div> : getProfilePhoto(entry.name) ? <img src={getProfilePhoto(entry.name)!} alt={entry.name} className="w-12 h-12 rounded-full border-2 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.4)] object-cover" data-id="element-581" /> : <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center text-white font-bold text-sm shadow-[0_0_12px_rgba(239,68,68,0.4)]" data-id="element-582">
                                            {getInitials(entry.name)}
                                          </div>}
                                      </div>
                                    </div>

                                    {/* Row 2: Visual Comparison Bar */}
                                    {(() => {
                              const total = currentUserEntry.value + entry.value;
                              let myPercent = currentUserEntry.value / total * 100;
                              if (isSpeed) {
                                myPercent = entry.value / total * 100;
                              }
                              myPercent = Math.max(10, Math.min(90, myPercent));
                              return <div className="relative h-12 w-full bg-navy-950 rounded-full overflow-hidden border border-white/[0.05] flex shadow-inner" data-id="element-583">
                                          <motion.div initial={{
                                  width: '50%'
                                }} animate={{
                                  width: `${myPercent}%`
                                }} transition={{
                                  type: 'spring',
                                  stiffness: 60,
                                  damping: 15
                                }} className="h-full bg-gradient-to-r from-blue-600 to-blue-500 relative flex items-center px-4" data-id="element-584">
                                            <span className="text-sm font-bold text-white drop-shadow-sm absolute left-4" data-id="element-585">
                                              {formatValue(currentUserEntry.value, dashboardType)}
                                            </span>
                                            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-blue-400/30 to-transparent" data-id="element-586" />
                                          </motion.div>
                                          <motion.div initial={{
                                  width: '50%'
                                }} animate={{
                                  width: `${100 - myPercent}%`
                                }} transition={{
                                  type: 'spring',
                                  stiffness: 60,
                                  damping: 15
                                }} className="h-full bg-gradient-to-r from-red-600 to-red-500 relative flex items-center justify-end px-4" data-id="element-587">
                                            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-red-400/30 to-transparent" data-id="element-588" />
                                            <span className="text-sm font-bold text-white absolute right-4" data-id="element-589">
                                              {formatValue(entry.value, dashboardType)}
                                            </span>
                                          </motion.div>
                                        </div>;
                            })()}

                                    {/* Row 3: Gap Summary */}
                                    {(() => {
                              const isAhead = isSpeed ? currentUserEntry.value < entry.value : currentUserEntry.value > entry.value;
                              const diff = Math.abs(currentUserEntry.value - entry.value);
                              const rankDiff = Math.abs(currentUserEntry.rank - entry.rank);
                              let diffStr = '';
                              let actionMsg = '';
                              if (dashboardType === 'volume') {
                                diffStr = diff >= 1000000 ? `$${(diff / 1000000).toFixed(1)}M` : `$${(diff / 1000).toFixed(0)}K`;
                                const loansNeeded = Math.ceil(diff / 250000);
                                actionMsg = isDivisionScope ? `Your division needs ~${diffStr} more volume to catch up` : `Close ~${loansNeeded} more loans at avg size to catch up`;
                              } else if (dashboardType === 'speed') {
                                diffStr = `${diff} days`;
                                actionMsg = isDivisionScope ? `Reduce division avg close time by ${diff} days` : `Reduce your avg close time by ${diff} days`;
                              } else {
                                diffStr = `${diff} units`;
                                actionMsg = isDivisionScope ? `Your division needs ${diff} more closed loans` : `Close ${diff} more loans to match`;
                              }
                              return <div className="flex items-center justify-center gap-4 bg-navy-800/40 py-2.5 px-6 rounded-xl border border-white/[0.03]" data-id="element-590">
                                          <div className={`text-sm font-bold ${isAhead ? 'text-accent-400' : 'text-amber-400'}`} data-id="element-591">
                                            {isAhead ? `Ahead by ${diffStr}` : `${diffStr} gap`}
                                          </div>
                                          <div className="w-1 h-1 rounded-full bg-slate-600" data-id="element-592" />
                                          <div className="text-sm text-slate-300 font-medium" data-id="element-593">
                                            {isAhead ? isDivisionScope ? 'Your division is leading — keep the momentum!' : 'You are leading — keep it up!' : actionMsg}
                                          </div>
                                          <div className="w-1 h-1 rounded-full bg-slate-600" data-id="element-594" />
                                          <div className="text-xs font-semibold text-slate-500 bg-navy-900 px-2 py-1 rounded-md border border-white/[0.05]" data-id="element-595">
                                            {rankDiff}{' '}
                                            {isDivisionScope ? 'division ranks' : `rank${rankDiff !== 1 ? 's' : ''}`}{' '}
                                            apart
                                          </div>
                                        </div>;
                            })()}
                                  </div>}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
                })}

                  {data.length === 0 && <div className="px-8 py-20 text-center text-lg text-slate-500" data-id="element-596">
                      No data available for this period.
                    </div>}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Footer spacer */}
          <div className="h-16" data-id="element-597" />
        </div>
      </div>
    </div>;
}